import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';

interface AuditLog {
  id: string;
  eventType: string;
  eventCategory: string;
  actorType: string;
  mspUserName?: string;
  mspUserEmail?: string;
  clientName?: string;
  ipAddress?: string;
  messageId?: string;
  actionResult: string;
  actionDetails: Record<string, unknown>;
  errorMessage?: string;
  createdAt: string;
}

interface AuditStats {
  totalEvents: number;
  byCategory: Record<string, number>;
  byResult: Record<string, number>;
}

export default function AuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [filters, setFilters] = useState({
    eventCategory: '',
    eventType: '',
    actionResult: '',
    startDate: '',
    endDate: '',
  });

  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getAuditLogs({
        page,
        limit: 50,
        eventCategory: filters.eventCategory || undefined,
        eventType: filters.eventType || undefined,
        actionResult: filters.actionResult || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setLogs(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleApplyFilters = () => {
    setPage(1);
    loadLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      eventCategory: '',
      eventType: '',
      actionResult: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Veuillez sélectionner une période pour l\'export');
      return;
    }

    try {
      const response = await adminApi.exportAuditLogs(filters.startDate, filters.endDate, format);
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${filters.startDate}-${filters.endDate}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getEventTypeLabel = (eventType: string): string => {
    const labels: Record<string, string> = {
      MSP_LOGIN: 'Connexion',
      MSP_LOGOUT: 'Déconnexion',
      MSP_CREATE_MESSAGE: 'Création message',
      MSP_VIEW_MESSAGE: 'Consultation message',
      MSP_REVOKE_MESSAGE: 'Révocation message',
      MSP_LIST_DEPOSITS: 'Liste dépôts',
      MSP_VIEW_DEPOSIT: 'Consultation dépôt',
      MSP_DELETE_DEPOSIT: 'Suppression dépôt',
      MSP_LIST_MESSAGES: 'Liste messages',
      MSP_VIEW_DASHBOARD: 'Dashboard',
      MSP_UPDATE_CONFIG: 'Config mise à jour',
      MSP_EXPORT_AUDIT: 'Export audit',
      CLIENT_DEPOSIT_CREATED: 'Dépôt créé',
      CLIENT_UPLOAD_FILE: 'Upload fichier',
      CLIENT_VIEW_MESSAGE: 'Lecture message',
      CLIENT_READ_MESSAGE: 'Marqué lu',
      CLIENT_DOWNLOAD_ATTACHMENT: 'Téléchargement',
      CLIENT_ACCESS_INVALID_TOKEN: 'Token invalide',
      CLIENT_EXCEED_MAX_READS: 'Lectures max dépassées',
      SYSTEM_STARTUP: 'Démarrage système',
      SYSTEM_SHUTDOWN: 'Arrêt système',
      SYSTEM_ERROR: 'Erreur système',
      SYSTEM_AUTO_CLEANUP: 'Nettoyage auto',
    };
    return labels[eventType] || eventType;
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'MSP_ACTION':
        return <span className="badge badge-info">MSP</span>;
      case 'CLIENT_ACTION':
        return <span className="badge badge-success">Client</span>;
      case 'SYSTEM_ACTION':
        return <span className="badge badge-neutral">Système</span>;
      default:
        return <span className="badge">{category}</span>;
    }
  };

  const getResultBadge = (result: string) => {
    switch (result) {
      case 'SUCCESS':
        return <span className="badge badge-success">Succès</span>;
      case 'FAILURE':
        return <span className="badge badge-error">Échec</span>;
      case 'PARTIAL':
        return <span className="badge badge-warning">Partiel</span>;
      default:
        return <span className="badge">{result}</span>;
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Logs d'audit</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleExport('csv')}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('json')}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            JSON
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Total événements</p>
          <p className="text-2xl font-bold text-blue-900">{stats?.totalEvents ?? 0}</p>
        </div>
        <div className="card bg-green-50 border-green-200">
          <p className="text-sm text-green-600 font-medium">Actions MSP</p>
          <p className="text-2xl font-bold text-green-900">{stats?.byCategory?.MSP_ACTION ?? 0}</p>
        </div>
        <div className="card bg-yellow-50 border-yellow-200">
          <p className="text-sm text-yellow-600 font-medium">Actions Client</p>
          <p className="text-2xl font-bold text-yellow-900">{stats?.byCategory?.CLIENT_ACTION ?? 0}</p>
        </div>
        <div className="card bg-red-50 border-red-200">
          <p className="text-sm text-red-600 font-medium">Échecs</p>
          <p className="text-2xl font-bold text-red-900">{stats?.byResult?.FAILURE ?? 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label htmlFor="filterCategory" className="label">Catégorie</label>
            <select
              id="filterCategory"
              value={filters.eventCategory}
              onChange={(e) => setFilters({ ...filters, eventCategory: e.target.value })}
              className="input"
            >
              <option value="">Toutes</option>
              <option value="MSP_ACTION">Actions MSP</option>
              <option value="CLIENT_ACTION">Actions Client</option>
              <option value="SYSTEM_ACTION">Actions Système</option>
            </select>
          </div>
          <div>
            <label htmlFor="filterResult" className="label">Résultat</label>
            <select
              id="filterResult"
              value={filters.actionResult}
              onChange={(e) => setFilters({ ...filters, actionResult: e.target.value })}
              className="input"
            >
              <option value="">Tous</option>
              <option value="SUCCESS">Succès</option>
              <option value="FAILURE">Échec</option>
            </select>
          </div>
          <div>
            <label htmlFor="filterStartDate" className="label">Date début</label>
            <input
              type="date"
              id="filterStartDate"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label htmlFor="filterEndDate" className="label">Date fin</label>
            <input
              type="date"
              id="filterEndDate"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="input"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleResetFilters}
              className="btn-secondary flex-1"
            >
              Réinitialiser
            </button>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="btn-primary flex-1"
            >
              Appliquer
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner spinner-lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Heure
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Événement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acteur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Résultat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500">Aucun log d'audit pour le moment</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Les événements apparaîtront ici au fur et à mesure des actions
                      </p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getCategoryBadge(log.eventCategory)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {getEventTypeLabel(log.eventType)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.mspUserName ? (
                          <span className="text-gray-900">{log.mspUserName}</span>
                        ) : log.clientName ? (
                          <span className="text-gray-600">{log.clientName}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getResultBadge(log.actionResult)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.ipAddress || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <button
                          type="button"
                          onClick={() => setSelectedLog(log)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Voir
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-outline"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-500">
              Page {page} sur {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-outline"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Détail de l'événement</h2>
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fermer"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Date/Heure</p>
                    <p className="text-gray-900">{formatDate(selectedLog.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Catégorie</p>
                    {getCategoryBadge(selectedLog.eventCategory)}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Type d'événement</p>
                  <p className="text-gray-900">{getEventTypeLabel(selectedLog.eventType)}</p>
                  <p className="text-xs text-gray-400">{selectedLog.eventType}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Acteur</p>
                    <p className="text-gray-900">
                      {selectedLog.mspUserName || selectedLog.clientName || 'Système'}
                    </p>
                    {selectedLog.mspUserEmail && (
                      <p className="text-xs text-gray-400">{selectedLog.mspUserEmail}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Résultat</p>
                    {getResultBadge(selectedLog.actionResult)}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Adresse IP</p>
                  <p className="text-gray-900">{selectedLog.ipAddress || 'Non disponible'}</p>
                </div>

                {selectedLog.messageId && (
                  <div>
                    <p className="text-sm text-gray-500">ID Message</p>
                    <p className="text-gray-900 font-mono text-xs">{selectedLog.messageId}</p>
                  </div>
                )}

                {selectedLog.errorMessage && (
                  <div>
                    <p className="text-sm text-gray-500">Message d'erreur</p>
                    <p className="text-red-600">{selectedLog.errorMessage}</p>
                  </div>
                )}

                {Object.keys(selectedLog.actionDetails || {}).length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Détails de l'action</p>
                    <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(selectedLog.actionDetails, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="btn-outline"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
